"use client";

import React, { useState, useEffect } from "react";
import { motion, type Variants } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";

const LIME = "#ccfc45";

const blocks = [
    {
        title: "Scaling Cloud Kitchens in Bengaluru & Noida",
        category: "Regional Growth",
        date: "Feb 27, 2026",
        read: "7 min read",
        excerpt: "How Restaubee's tech stack is powering the next wave of dark kitchens in India's leading tech hubs.",
        span: "md:col-span-2",
        large: true,
    },
    {
        title: "Why Punjab is Switching to Restaubee POS",
        category: "Success Stories",
        date: "Feb 25, 2026",
        read: "5 min read",
        excerpt: "From Amritsar to Chandigarh, traditional restaurants are adopting modern billing solutions.",
        span: "md:col-span-1",
        large: false,
    },
    {
        title: "QR Code Ordering: Scaling Cafe Culture in Delhi & Mumbai",
        category: "Product Innovation",
        date: "Feb 20, 2026",
        read: "5 min read",
        excerpt: "How contactless menus and instant billing are revolutionizing the dining experience for Gen-Z.",
        span: "md:col-span-1",
        large: false,
    },
    {
        title: "The Future of Fine Dining in Kerala & Chennai",
        category: "Industry Trends",
        date: "Feb 15, 2026",
        read: "6 min read",
        excerpt: "Exploring the rise of experiential dining and the role of integrated CRM in South Indian states.",
        span: "md:col-span-2",
        large: false,
    },
];

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export function BlogContent() {
    const [mounted, setMounted] = useState(false);
    const { resolvedTheme } = useTheme();
    useEffect(() => { setMounted(true); }, []);
    
    const isDark = mounted ? (resolvedTheme === "dark") : true;

    const bgColor = "bg-background";
    const textColor = "text-foreground";
    const subTextColor = "text-muted-foreground";
    const borderColor = "border-border-subtle";
    const cardBgNormal = "bg-surface";

    return (
        <main className={cn(
            "min-h-screen pt-28 pb-24 px-4 overflow-hidden transition-colors duration-500",
            bgColor,
            textColor
        )}>
            {!mounted ? null : (
                <>
                    {/* Glow */}
                    <div
                        className="fixed top-0 right-0 w-[500px] h-[500px] rounded-full blur-[180px] pointer-events-none opacity-[0.06]"
                        style={{ background: LIME }}
                    />

                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-7xl mx-auto mb-20"
                    >
                        <p className="text-[11px] font-bold uppercase tracking-[0.35em] mb-6" style={{ color: LIME }}>
                            Latest Editorial
                        </p>
                        <h1 className="text-7xl md:text-9xl font-black tracking-tighter leading-none">
                            Insights &<br />
                            <span style={{ color: LIME }}>Stories.</span>
                            <span className="sr-only"> — Restaubee Blog</span>
                        </h1>
                    </motion.div>

                    {/* Bento-style Blog Grid */}
                    <motion.div
                        initial="hidden"
                        animate="show"
                        transition={{ staggerChildren: 0.1 }}
                        className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5"
                    >
                        {blocks.map((blog, i) => (
                            <motion.article
                                key={i}
                                variants={itemVariants}
                                whileHover={{ y: -6 }}
                                className={cn(
                                    "relative rounded-2xl overflow-hidden border cursor-pointer group shadow-sm transition-all duration-300",
                                    blog.span,
                                    i === 0 ? "border-transparent" : borderColor,
                                    i === 0 ? "" : cardBgNormal
                                )}
                                style={{ background: i === 0 ? LIME : undefined }}
                            >
                                <div className="p-8 flex flex-col h-full min-h-[260px]">
                                    {/* Category */}
                                    <div className="flex items-center gap-3 mb-auto">
                                        <span
                                            className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full"
                                            style={{
                                                background: i === 0 ? "rgba(0,0,0,0.1)" : LIME + "18",
                                                color: i === 0 ? "#000" : LIME,
                                            }}
                                        >
                                            {blog.category}
                                        </span>
                                    </div>

                                    <div className="mt-8">
                                        <h2
                                            className={cn("font-black text-2xl md:text-3xl tracking-tighter leading-tight mb-3", i === 0 ? "text-black" : textColor)}
                                        >
                                            {blog.title}
                                        </h2>
                                        <p
                                            className={cn("text-sm leading-relaxed mb-6 font-medium", i === 0 ? "text-black/60" : "text-muted-foreground")}
                                        >
                                            {blog.excerpt}
                                        </p>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div
                                            className={cn("text-xs font-bold", i === 0 ? "text-black/50" : "text-muted-foreground/60")}
                                        >
                                            {blog.date} · {blog.read}
                                        </div>
                                        <div
                                            className={cn(
                                                "w-9 h-9 rounded-full flex items-center justify-center transition-transform group-hover:translate-x-1",
                                                i === 0 ? "bg-black/10 text-black" : "bg-lime-500/10 text-lime-500"
                                            )}
                                        >
                                            <ArrowRight size={16} />
                                        </div>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </motion.div>
                </>
            )}
        </main>
    );
}
